import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert, Button, Card, CardBody, Col, Container, Input, Label, Row, Spinner, Table,
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';
import useAuth from '../Personal Attendance/useAuth';
import {
    DashboardPageHeader, EmptyState, ResponsiveTabs, StatusBadge, useDebouncedValue,
} from '../../Components/Common/DashboardKit';

// ─────────────────────────────────────────────────────────────────────────────
// Go-live parity (2026-09-24): India accounts (owner requirement 2026-09-29 —
// DI and MAFIA Aux run for India AND Florida with one dashboard login).
//
// HR > India Employees.
//   Tab 1 "Add from Epicor"  — India's employees live in Epicor company SN.
//         Pick people, pick the role, "Create accounts": each person gets a
//         dashboard account and a login number (the India number, or with a 2
//         in front when that number already belongs to a Florida employee).
//   Tab 2 "India accounts"   — the accounts that exist, with their status.
//   Tab 3 "Florida accounts of India people" (owner decision 2026-09-29) —
//         some India people had a dashboard account before India accounts
//         existed. Documents, approvals and tasks hang on its number, so the
//         account is SWITCHED to an India account and keeps its login number.
//
// An India account has MAFIA Aux, Digital Inspection, DMS and its tasks —
// no time clock, and HR does not see it in the Florida lists.
//
// Backend: api/india-employees/* (HR or IT Admin). Nothing here writes to
// Epicor. A new account has no password: the person gets one through Access
// Management > Account Security (setup code / link).
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
    { key: 'epicor', label: 'Add from Epicor', icon: 'ri-user-add-line' },
    { key: 'accounts', label: 'India accounts', icon: 'ri-team-line' },
    { key: 'florida', label: 'Florida accounts of India people', icon: 'ri-arrow-left-right-line' },
];

const INDIA_ACCOUNT_MEANS =
    'An India account can open MAFIA Aux, Digital Inspection, DMS and its own tasks. ' +
    'It has no time clock, and it is left out of the Florida HR, payroll and approval lists.';

const IMPORT_BATCH = 25;   // people per request (the API takes up to 100)

const RESULT_BADGES = {
    created: { status: 'ok', label: 'Created' },
    alreadyHasAccount: { status: 'neutral', label: 'Already has an account' },
    notInEpicor: { status: 'warn', label: 'Not in Epicor' },
    notActive: { status: 'warn', label: 'Not active in Epicor' },
    failed: { status: 'danger', label: 'Failed' },
};

const rateType = (code) => {
    const c = String(code || '').toUpperCase();
    if (c === 'IDL') return 'Salaried (IDL)';
    if (c === 'DL') return 'Hourly (DL)';
    return code || '—';
};

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;

/* ═══════════════════════════ Tab 1 — Add from Epicor ═══════════════════════ */
const AddFromEpicor = ({ onCreated }) => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 400);
    const [onlyWithoutAccount, setOnlyWithoutAccount] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [data, setData] = useState({ items: [], total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [roles, setRoles] = useState({ roles: [], defaultRoleId: null, canChoose: false, message: '' });
    const [roleId, setRoleId] = useState('');
    const [selected, setSelected] = useState({});   // indiaNumber → name
    const [creating, setCreating] = useState(false);
    const [results, setResults] = useState(null);   // [{ indiaNumber, name, result, dashboardNumber, message }]

    const latest = useRef(0);   // only the newest request may fill the table
    const load = useCallback(async () => {
        const mine = ++latest.current;
        setLoading(true); setError('');
        try {
            const r = await api.get('india-employees/epicor', {
                params: { search: debouncedSearch, page, pageSize, onlyWithoutAccount },
            });
            if (mine !== latest.current) return;
            setData({
                items: Array.isArray(r.data?.items) ? r.data.items : [],
                total: r.data?.total ?? 0,
                totalPages: r.data?.totalPages ?? 1,
            });
        } catch (e) {
            if (mine !== latest.current) return;
            setData({ items: [], total: 0, totalPages: 1 });
            setError(apiMessage(e, "India's employee list could not be loaded."));
        } finally {
            if (mine === latest.current) setLoading(false);
        }
    }, [debouncedSearch, page, pageSize, onlyWithoutAccount]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [debouncedSearch, pageSize, onlyWithoutAccount]);

    useEffect(() => {
        api.get('india-employees/roles')
            .then((r) => {
                const d = r.data || {};
                const list = Array.isArray(d.roles) ? d.roles : [];
                setRoles({ roles: list, defaultRoleId: d.defaultRoleId ?? null, canChoose: !!d.canChoose, message: d.message || '' });
                const preset = d.defaultRoleId ?? (list.length === 1 ? list[0].roleId : null);
                if (preset != null && list.some((x) => x.roleId === preset)) setRoleId(String(preset));
            })
            .catch((e) => setRoles({ roles: [], defaultRoleId: null, canChoose: false, message: apiMessage(e, 'The role list could not be loaded.') }));
    }, []);

    const selectable = data.items.filter((p) => p.canCreate);
    const allOnPageSelected = selectable.length > 0 && selectable.every((p) => selected[p.indiaNumber] !== undefined);
    const selectedNumbers = Object.keys(selected);

    const toggle = (person) => setSelected((prev) => {
        const next = { ...prev };
        if (next[person.indiaNumber] !== undefined) delete next[person.indiaNumber];
        else next[person.indiaNumber] = person.name;
        return next;
    });
    const togglePage = () => setSelected((prev) => {
        const next = { ...prev };
        selectable.forEach((p) => {
            if (allOnPageSelected) delete next[p.indiaNumber];
            else next[p.indiaNumber] = p.name;
        });
        return next;
    });

    const createAccounts = async () => {
        if (selectedNumbers.length === 0) { toast.info('Choose at least one person.'); return; }
        if (!roleId) { toast.info('Choose the role first.'); return; }
        const roleName = roles.roles.find((r) => String(r.roleId) === String(roleId))?.roleName || '';
        if (!window.confirm(`Create a dashboard account for ${selectedNumbers.length} India employee(s) with the role "${roleName}"?\n\nNothing is changed in Epicor. The accounts have no password yet.`)) return;

        setCreating(true);
        const all = [];
        try {
            for (let i = 0; i < selectedNumbers.length; i += IMPORT_BATCH) {
                const batch = selectedNumbers.slice(i, i + IMPORT_BATCH);
                try {
                    const r = await api.post('india-employees/import', { empIds: batch, roleId: Number(roleId) });
                    all.push(...(Array.isArray(r.data?.results) ? r.data.results : []));
                } catch (e) {
                    // The API's refusal, as it is, for every person of this batch.
                    const message = apiMessage(e, 'No answer from the server. Look at the "India accounts" tab before trying again.');
                    batch.forEach((n) => all.push({ indiaNumber: n, name: selected[n] || '', result: 'failed', dashboardNumber: null, message }));
                }
            }
            setResults(all);
            const created = all.filter((x) => x.result === 'created').length;
            if (created > 0) toast.success(`${created} account(s) created.`);
            else toast.warn('No account was created — see the result list.');
            setSelected({});
            if (created > 0 && onCreated) onCreated();
            await load();
        } finally {
            setCreating(false);
        }
    };

    return (
        <div>
            {results && (
                <Card className="border mb-3">
                    <CardBody>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="mb-0">Result — {results.filter((x) => x.result === 'created').length} of {results.length} created</h6>
                            <Button color="light" size="sm" onClick={() => setResults(null)}>Close</Button>
                        </div>
                        <div className="text-muted small mb-2">
                            The login number is what the employee types on the sign-in page. New accounts have no
                            password: issue a setup code or link in Access Management &gt; Account Security.
                        </div>
                        <Table className="table-sm align-middle mb-0" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 120 }}>India number</th>
                                    <th>Name</th>
                                    <th style={{ width: 130 }}>Login number</th>
                                    <th style={{ width: 200 }}>Result</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((x, i) => {
                                    const badge = RESULT_BADGES[x.result] || RESULT_BADGES.failed;
                                    return (
                                        <tr key={`${x.indiaNumber}-${i}`}>
                                            <td>{x.indiaNumber}</td>
                                            <td>{x.name || '—'}</td>
                                            <td className="fw-semibold">{x.dashboardNumber || '—'}</td>
                                            <td><StatusBadge status={badge.status} label={badge.label} /></td>
                                            <td className="text-muted small" style={{ whiteSpace: 'normal' }}>{x.message}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            )}

            <Row className="g-2 align-items-end mb-3">
                <Col lg={4} md={6}>
                    <Label className="form-label small text-muted mb-1" for="india-epicor-search">Search Epicor (name or India number)</Label>
                    <Input id="india-epicor-search" type="search" placeholder="name or India number"
                        value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col lg={3} md={6}>
                    <div className="form-check mb-2">
                        <Input id="india-only-without" type="checkbox" className="form-check-input"
                            checked={onlyWithoutAccount} onChange={(e) => setOnlyWithoutAccount(e.target.checked)} />
                        <Label className="form-check-label" for="india-only-without">Only people without an account</Label>
                    </div>
                </Col>
                <Col lg={3} md={6}>
                    <Label className="form-label small text-muted mb-1" for="india-role">Role of the new accounts</Label>
                    <Input id="india-role" type="select" value={roleId} onChange={(e) => setRoleId(e.target.value)}
                        disabled={roles.roles.length === 0 || (!roles.canChoose && roles.roles.length === 1)}>
                        <option value="">Select role</option>
                        {roles.roles.map((r) => <option key={r.roleId} value={String(r.roleId)}>{r.roleName}</option>)}
                    </Input>
                </Col>
                <Col lg={2} md={6} className="text-lg-end">
                    <Button color="primary" onClick={createAccounts}
                        disabled={creating || selectedNumbers.length === 0 || !roleId}>
                        {creating ? <><Spinner size="sm" className="me-1" />Creating…</> : `Create accounts (${selectedNumbers.length})`}
                    </Button>
                </Col>
            </Row>

            {roles.message && <Alert color="warning" className="py-2">{roles.message}</Alert>}
            {error && <Alert color="danger" className="py-2">{error}</Alert>}

            <Table className="table-hover align-middle mb-0" responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 44 }}>
                            <Input type="checkbox" aria-label="Select everybody on this page"
                                checked={allOnPageSelected} disabled={selectable.length === 0} onChange={togglePage} />
                        </th>
                        <th style={{ width: 120 }}>India number</th>
                        <th>Name</th>
                        <th>Department</th>
                        <th style={{ width: 130 }}>Type</th>
                        <th>E-mail</th>
                        <th>Dashboard account</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={7} className="text-center text-muted py-4"><Spinner size="sm" className="me-2" />Reading Epicor…</td></tr>
                    )}
                    {!loading && data.items.length === 0 && (
                        <tr><td colSpan={7}>
                            <EmptyState compact icon="ri-user-search-line"
                                title={error ? 'Nothing to show' : 'Nobody found'}
                                message={error ? undefined : (onlyWithoutAccount
                                    ? 'No active India employee without an account matches the search.'
                                    : 'No active India employee matches the search.')} />
                        </td></tr>
                    )}
                    {!loading && data.items.map((p) => (
                        <tr key={p.indiaNumber}>
                            <td>
                                <Input type="checkbox" aria-label={`Select ${p.name}`}
                                    checked={selected[p.indiaNumber] !== undefined}
                                    disabled={!p.canCreate} onChange={() => toggle(p)} />
                            </td>
                            <td>{p.indiaNumber}</td>
                            <td>{p.name || '—'}</td>
                            <td>{p.department ? (p.departmentName ? `${p.department} — ${p.departmentName}` : p.department) : '—'}</td>
                            <td>{rateType(p.expenseCode)}</td>
                            <td>{p.email || '—'}</td>
                            <td style={{ whiteSpace: 'normal' }}>
                                {p.hasAccount
                                    ? <StatusBadge status="ok" label={`Has account · ${p.dashboardNumber}`} />
                                    : p.canCreate
                                        ? <StatusBadge status={p.numberClash ? 'warn' : 'neutral'} icon="ri-login-box-line"
                                            label={`Login number will be ${p.dashboardNumber}`} />
                                        : <StatusBadge status="danger" label="Cannot be created" />}
                                {(p.numberClash || (!p.hasAccount && !p.canCreate)) && p.note && (
                                    <div className="text-muted small mt-1">{p.note}</div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                <span className="text-muted small">
                    {data.total} {onlyWithoutAccount ? 'without an account' : 'active in Epicor'} · page {page} of {Math.max(1, data.totalPages)}
                    {selectedNumbers.length > 0 && <> · <strong>{selectedNumbers.length} selected</strong>{' '}
                        <Button color="link" size="sm" className="p-0 align-baseline" onClick={() => setSelected({})}>clear</Button></>}
                </span>
                <div className="d-flex align-items-center gap-2">
                    <Input type="select" bsSize="sm" style={{ width: 'auto' }} aria-label="Rows per page"
                        value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                        {[25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
                    </Input>
                    <Button color="light" size="sm" disabled={loading || page <= 1} onClick={() => setPage((x) => Math.max(1, x - 1))}>Previous</Button>
                    <Button color="light" size="sm" disabled={loading || page >= data.totalPages} onClick={() => setPage((x) => x + 1)}>Next</Button>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════ Tab 2 — India accounts ════════════════════════ */
const ACCOUNTS_PAGE = 50;

const IndiaAccounts = ({ reloadKey, onChanged }) => {
    const auth = useAuth();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 400);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState('');

    const canOpenAccountSecurity = auth.isItAdmin || auth.canAccessModule('admin.accountSecurity');
    const canOpenMafia = auth.isItAdmin || auth.canAccessModule('app.mafia');

    const latest = useRef(0);   // only the newest request may fill the table
    const load = useCallback(async () => {
        const mine = ++latest.current;
        setLoading(true); setError('');
        try {
            const r = await api.get('india-employees/accounts', { params: { search: debouncedSearch } });
            if (mine !== latest.current) return;
            setRows(Array.isArray(r.data?.items) ? r.data.items : []);
        } catch (e) {
            if (mine !== latest.current) return;
            setRows([]);
            setError(apiMessage(e, 'The India accounts could not be loaded.'));
        } finally {
            if (mine === latest.current) setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => { load(); }, [load, reloadKey]);
    useEffect(() => { setPage(1); }, [debouncedSearch]);

    const totalPages = Math.max(1, Math.ceil(rows.length / ACCOUNTS_PAGE));
    const visible = useMemo(() => rows.slice((page - 1) * ACCOUNTS_PAGE, page * ACCOUNTS_PAGE), [rows, page]);

    // Only for an account that was switched (it existed before as a Florida account).
    const switchBack = async (row) => {
        if (!window.confirm(`Make ${row.name} (login number ${row.dashboardNumber}) a Florida account again?` +
            '\n\nThe account keeps its number, its role and its password.' +
            '\nIt is shown again in the Florida HR, payroll and approval lists, and it gets everything its role gives.')) return;
        setBusy(row.dashboardNumber);
        try {
            const r = await api.post('india-employees/switch-back', { empId: row.dashboardNumber });
            toast.success(r.data?.message || 'Done.');
            if (onChanged) onChanged();
            await load();
        } catch (e) {
            toast.error(apiMessage(e, 'The account could not be changed.'), { autoClose: 9000 });
        } finally {
            setBusy('');
        }
    };

    // The existing HR endpoints; they keep the account an India account.
    const setActive = async (row, activate) => {
        const what = activate ? 'Reactivate' : 'Deactivate';
        if (!window.confirm(`${what} the dashboard account of ${row.name} (login number ${row.dashboardNumber})?` +
            (activate ? '' : '\n\nThe person can no longer sign in to the dashboard.') +
            '\n\nEpicor (company SN) is changed too only when the India sync to Epicor is switched on; the answer says what was done.')) return;
        setBusy(row.dashboardNumber);
        try {
            const r = await api.put(activate ? 'registration/reactivate' : 'registration/set-inactive',
                { EmpID: parseInt(row.dashboardNumber, 10) });
            if (r.data?.warning) toast.warning(r.data.warning, { autoClose: 8000 });
            else toast.success(r.data?.message || `${what}d.`);
            await load();
        } catch (e) {
            toast.error(apiMessage(e, `${what} failed.`));
        } finally {
            setBusy('');
        }
    };

    return (
        <div>
            <Row className="g-2 align-items-end mb-3">
                <Col lg={4} md={6}>
                    <Label className="form-label small text-muted mb-1" for="india-accounts-search">Search (name, login number or India number)</Label>
                    <Input id="india-accounts-search" type="search" placeholder="name or login number"
                        value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col lg={8} md={6} className="text-md-end">
                    {canOpenAccountSecurity
                        ? <Link to="/admin/access?tab=security" className="btn btn-soft-primary btn-sm me-2">
                            <i className="ri-lock-password-line align-bottom me-1" />Account Security (setup code / link)
                        </Link>
                        : <span className="text-muted small me-2">Setup codes and links: ask IT (Access Management &gt; Account Security).</span>}
                    {canOpenMafia && (
                        <Link to="/mafia" className="btn btn-soft-primary btn-sm">
                            <i className="ri-key-2-line align-bottom me-1" />MAFIA Aux (Permissions Admin)
                        </Link>
                    )}
                </Col>
            </Row>

            {error && <Alert color="danger" className="py-2">{error}</Alert>}

            <div className="text-muted small mb-2">{INDIA_ACCOUNT_MEANS}</div>

            <Table className="table-hover align-middle mb-0" responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 130 }}>Login number</th>
                        <th style={{ width: 120 }}>India number</th>
                        <th>Name</th>
                        <th style={{ width: 110 }}>Dept</th>
                        <th style={{ width: 110 }}>Status</th>
                        <th>Role</th>
                        <th style={{ width: 120 }}>Password</th>
                        <th style={{ width: 120 }} title="The level that counts in the India MAFIA database">MAFIA level (India)</th>
                        <th style={{ width: 230 }} className="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={9} className="text-center text-muted py-4"><Spinner size="sm" className="me-2" />Loading…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                        <tr><td colSpan={9}>
                            <EmptyState compact icon="ri-team-line" title={error ? 'Nothing to show' : 'No India account'}
                                message={error ? undefined : (debouncedSearch
                                    ? 'No India account matches the search.'
                                    : 'Create the first ones on the "Add from Epicor" tab.')} />
                        </td></tr>
                    )}
                    {!loading && visible.map((a) => {
                        const active = a.status === 'A';
                        return (
                            <tr key={a.dashboardNumber}>
                                <td className="fw-semibold">
                                    {a.dashboardNumber}
                                    {a.switched && (
                                        <div className="text-muted small fw-normal" title="This account existed before as a Florida account. It was switched to India and kept its login number.">
                                            kept its number
                                        </div>
                                    )}
                                </td>
                                <td>{a.indiaNumber}</td>
                                <td>{a.name || '—'}</td>
                                <td>{a.department || '—'}</td>
                                <td><StatusBadge status={active ? 'ok' : 'neutral'} icon={active ? undefined : 'ri-pause-circle-line'}
                                    label={active ? 'Active' : `Not active (${a.status || 'blank'})`} /></td>
                                <td>{a.roleName || <span className="text-danger">No role</span>}</td>
                                <td>{a.hasPassword
                                    ? <StatusBadge status="ok" label="Set" />
                                    : <StatusBadge status="warn" label="Not set" />}</td>
                                <td>{a.mafiaLevel === null || a.mafiaLevel === undefined ? 'None' : `Level ${a.mafiaLevel}`}</td>
                                <td className="text-end">
                                    {a.switched && (
                                        <Button size="sm" color="soft-secondary" className="me-1"
                                            disabled={busy === a.dashboardNumber} onClick={() => switchBack(a)}>
                                            Back to Florida
                                        </Button>
                                    )}
                                    <Button size="sm" color={active ? 'soft-danger' : 'soft-success'}
                                        disabled={busy === a.dashboardNumber} onClick={() => setActive(a, !active)}>
                                        {busy === a.dashboardNumber ? 'Working…' : (active ? 'Deactivate' : 'Reactivate')}
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                <span className="text-muted small">{rows.length} India account(s) · page {page} of {totalPages}</span>
                <div className="d-flex align-items-center gap-2">
                    <Button color="light" size="sm" disabled={page <= 1} onClick={() => setPage((x) => Math.max(1, x - 1))}>Previous</Button>
                    <Button color="light" size="sm" disabled={page >= totalPages} onClick={() => setPage((x) => x + 1)}>Next</Button>
                </div>
            </div>
        </div>
    );
};

/* ═══════════ Tab 3 — Florida accounts of India people (the switch) ═════════ */
// Owner decision (2026-09-29). An India person who had a dashboard account
// before India accounts existed keeps that account: documents, approvals and
// tasks hang on its login number. The account is SWITCHED — it becomes an
// India account and keeps its number, role and password.
const FloridaAccounts = ({ reloadKey, onSwitched }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState('');

    const latest = useRef(0);   // only the newest request may fill the table
    const load = useCallback(async () => {
        const mine = ++latest.current;
        setLoading(true); setError('');
        try {
            const r = await api.get('india-employees/florida-accounts');
            if (mine !== latest.current) return;
            setRows(Array.isArray(r.data?.items) ? r.data.items : []);
        } catch (e) {
            if (mine !== latest.current) return;
            setRows([]);
            setError(apiMessage(e, 'The list could not be loaded.'));
        } finally {
            if (mine === latest.current) setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, reloadKey]);

    const rowKey = (row) => `${row.empId}-${row.indiaNumber}`;

    const switchToIndia = async (row) => {
        const warnings = Array.isArray(row.warnings) && row.warnings.length > 0
            ? `\n\nPlease look at this first:\n- ${row.warnings.join('\n- ')}`
            : '';
        if (!window.confirm(
            `Make ${row.name} (login number ${row.empId}) an India account?` +
            `\n\nIndia employee ${row.indiaNumber} in Epicor: ${row.indiaName}.` +
            '\n\nThe account keeps its login number, its role and its password. Its documents, approvals and tasks stay with it.' +
            `\n\n${INDIA_ACCOUNT_MEANS}` +
            warnings +
            '\n\nNothing is changed in Epicor. You can undo this on the "India accounts" tab (Back to Florida).')) return;

        setBusy(rowKey(row));
        try {
            const r = await api.post('india-employees/switch', { empId: row.empId, indiaNumber: row.indiaNumber });
            toast.success(r.data?.message || 'Done.', { autoClose: 8000 });
            if (onSwitched) onSwitched();
            await load();
        } catch (e) {
            toast.error(apiMessage(e, 'The account could not be switched.'), { autoClose: 9000 });
        } finally {
            setBusy('');
        }
    };

    return (
        <div>
            <Alert color="info" className="py-2">
                <div className="fw-semibold mb-1">People of the India company who have a Florida account already</div>
                <div className="small">
                    These accounts carry the name of an active employee of Epicor company SN. Switch an account when the
                    person works for India. The account keeps its login number, so nothing the person wrote, approved or
                    was given is lost. {INDIA_ACCOUNT_MEANS}
                </div>
            </Alert>

            {error && <Alert color="danger" className="py-2">{error}</Alert>}

            <Table className="table-hover align-middle mb-0" responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 130 }}>Login number</th>
                        <th>Name</th>
                        <th style={{ width: 90 }}>Dept</th>
                        <th style={{ width: 130 }}>Type</th>
                        <th>Role</th>
                        <th style={{ width: 120 }}>India number</th>
                        <th>In Epicor (company SN)</th>
                        <th style={{ width: 170 }} className="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={8} className="text-center text-muted py-4"><Spinner size="sm" className="me-2" />Reading Epicor…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                        <tr><td colSpan={8}>
                            <EmptyState compact icon="ri-arrow-left-right-line"
                                title={error ? 'Nothing to show' : 'No account to switch'}
                                message={error ? undefined
                                    : 'No active Florida account carries the name of an active India employee.'} />
                        </td></tr>
                    )}
                    {!loading && rows.map((row) => (
                        <tr key={rowKey(row)}>
                            <td className="fw-semibold">{row.empId}</td>
                            <td style={{ whiteSpace: 'normal' }}>
                                {row.name || '—'}
                                {Array.isArray(row.warnings) && row.warnings.map((w, i) => (
                                    <div key={i} className="text-warning small mt-1">
                                        <i className="ri-error-warning-line align-bottom me-1" />{w}
                                    </div>
                                ))}
                                {!row.canSwitch && row.note && (
                                    <div className="text-danger small mt-1">{row.note}</div>
                                )}
                            </td>
                            <td>{row.department || '—'}</td>
                            <td>{rateType(row.expenseCode)}</td>
                            <td>{row.roleName || <span className="text-danger">No role</span>}</td>
                            <td>{row.indiaNumber}</td>
                            <td style={{ whiteSpace: 'normal' }}>
                                {row.indiaName || '—'}
                                {row.indiaDepartment && (
                                    <div className="text-muted small">
                                        {row.indiaDepartmentName ? `${row.indiaDepartment} — ${row.indiaDepartmentName}` : row.indiaDepartment}
                                    </div>
                                )}
                            </td>
                            <td className="text-end">
                                {row.canSwitch
                                    ? (
                                        <Button size="sm" color="soft-primary" disabled={busy !== ''} onClick={() => switchToIndia(row)}>
                                            {busy === rowKey(row) ? 'Working…' : 'Make India account'}
                                        </Button>
                                    )
                                    : <StatusBadge status="neutral" label="Cannot be switched" />}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <div className="text-muted small mt-3">
                {rows.length} account(s). An account with the IT Admin role is never an India account: an IT Admin sees everything.
            </div>
        </div>
    );
};

/* ═══════════════════════════════════ Page ══════════════════════════════════ */
const IndiaEmployees = () => {
    document.title = 'India Employees | SSW Technologies Dashboard';

    const [tab, setTab] = useState('epicor');
    const [accountsVersion, setAccountsVersion] = useState(0);
    const accountsChanged = () => setAccountsVersion((v) => v + 1);

    return (
        <React.Fragment>
            <ToastContainer closeButton={false} position="top-right" />
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-global-line"
                        eyebrow="People"
                        title="India Employees"
                        subtitle="Dashboard accounts for the people of Epicor company SN — so they can sign in and use Digital Inspection, MAFIA Aux and DMS."
                        tone="violet"
                        breadcrumb={[
                            { label: 'Dashboards', to: '/' },
                            { label: 'Human Resource', to: '/humanresource' },
                            { label: 'India Employees' },
                        ]}
                    />

                    <Card className="border-0 shadow-sm">
                        <CardBody>
                            <ResponsiveTabs tabs={TABS} activeKey={tab} onChange={setTab} color="primary" />
                            {tab === 'epicor' && <AddFromEpicor onCreated={accountsChanged} />}
                            {tab === 'accounts' && <IndiaAccounts reloadKey={accountsVersion} onChanged={accountsChanged} />}
                            {tab === 'florida' && <FloridaAccounts reloadKey={accountsVersion} onSwitched={accountsChanged} />}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default IndiaEmployees;
